import React, {
  useEffect,
  useState,
  forwardRef, 
  useImperativeHandle
} from "react";
import moment from 'moment';

import { Space, Button, Spin } from "antd";
import { all as AllStats } from "../../api/Stats";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { defineColor } from '../../utils';

const WeekTab = forwardRef((props, ref) => {
  const { selectedDate, careUnitIds } = props;

  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState([]);
  const [stats, setStats] = useState([]);

  const [promise, setPromise] = useState(null);

  const getStartDateWeek = (date) => moment(date).startOf('isoWeek');

  /**
   *
   * @param {*} filtersStorage
   */
  const loadFiltersStorage = (filtersStorage) => {
    if (!filtersStorage) {
      setReady(true);
      return;
    }

    filtersStorage = JSON.parse(filtersStorage);

    if (filtersStorage.hasOwnProperty('days')) {
      let storedDays = filtersStorage.days.map(date => moment(date));
      setDays(storedDays);
    }

    setReady(true);
  };

  /**
   *
   * @param {*} days
   */
  const storeFiltersStorage = (days) => {
    let filtersCalendarWeek = JSON.stringify({ "days": days });

    localStorage.setItem('filters_calendar_week', filtersCalendarWeek);
};

  /**
   *
   * @param {*} startDate
   */
  const defineDays = (startDate) => {
    let weekStart = getStartDateWeek(startDate);
    let days = [];

    for (let i = 0; i <= 6; i++) {
      days.push(moment(weekStart).add(i, 'days'));
    }

    setDays(days);
    storeFiltersStorage(days);

    return days;
  };

  /**
   * 
   * @param {*} startDate 
   * @param {*} endDate 
   */
  const loadStats = (startDate, endDate) => {
    setLoading(true);

    if (promise) {
        promise.cancel();
    }

    let promiseStats = getPromiseStats(startDate, endDate);
    setPromise(promiseStats);

    promiseStats.then((data) => {
      data['stats']['stats'].map((statOfDay) => {
        statOfDay['className'] = defineColor(statOfDay['occupancy_rate']);
        return statOfDay;
      })

      setStats(data['stats']['stats']);
      setPromise(null);
      setLoading(false);
    }).catch(() => {
        setTimeout(() => {
            setLoading(false);
        }, 20000);
    });
  };

  /**
   *
   * @param {*} startDate
   * @param {*} endDate
   */
  const getPromiseStats = (startDate, endDate) => {
    let controller = new AbortController();
    let signal = controller.signal;

    let payload = {};
    if (careUnitIds.length) {
      payload['care_unit_ids'] = careUnitIds;
    }

    let promiseStats = AllStats(startDate, endDate, payload, signal);
    promiseStats.cancel = () => controller.abort();

    return promiseStats;
  };

  /**
   *
   * @param {*} newDate
   */
  const loadStatsByWeek = (newDate) => {
    let newDays = defineDays(newDate);
    loadStats(newDays[0], newDays[newDays.length - 1]);
  };

  /**
   *
   */
  const reload = () => {
    loadStats(days[0], days[days.length - 1]);
  }

  /**
   * 
   */
  const onClickPrevWeekHandler = () => {
    let firstDay = days[0].subtract(1, 'weeks');
    loadStatsByWeek(firstDay);
  };

  /**
   * 
   */
  const onClickNextWeekHandler = () => {
    let firstDay = days[0].add(1, 'weeks');
    loadStatsByWeek(firstDay);
  };

  /**
   * 
   * @param {*} value 
   */
  const onSelect = (value) => {
    props.selectDate(value);
  };

  useEffect(async () => {
    let filtersStorage = localStorage.getItem('filters_calendar_week', null);
    loadFiltersStorage(filtersStorage);
  }, []);

  useEffect(() => {
    if (!ready) {
      return;
    }

    const weekStart = getStartDateWeek(selectedDate);

    if ((days.length === 0) || days[0].format('YYYYMMDD') !== weekStart.format('YYYYMMDD')) {
      loadStatsByWeek(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!ready) {
      return;
    }

    loadStatsByWeek(selectedDate);
  }, [careUnitIds]);

  useEffect(() => {
    if (!ready) {
      return;
    }

    if (days.length > 0) {
       loadStats(days[0], days[days.length - 1]);
    } else {
      loadStatsByWeek(selectedDate);
    }
  }, [ready]);

  useImperativeHandle(ref, () => ({
    reload: () => {
      reload();
    },
  }));

  let weekNumber = (ready && days.length > 0) ? days[0].format('WW') : null;

  let totals = {
    'start': 0,
    'end': 0,
    'current': 0,
    'total_end': 0
  };

  stats.forEach((row) => {
    totals['start'] += row['start'];
    totals['end'] += row['end'];
    totals['current'] += row['current'];
    totals['total_end'] += row['total_end'];
  });

  return (
    <>
      <Space style={{ padding: "8px", width: "100%" }}>
        <Button
          type="primary"
          shape="circle"
          icon={<LeftOutlined />}
          onClick={onClickPrevWeekHandler}
        />
        <Button
          type="primary"
          shape="circle"
          icon={<RightOutlined />}
          onClick={onClickNextWeekHandler}
        />
      </Space>
      <Spin spinning={loading}>
        <div id="week-calendar" className="week-calendar">
          <div className="grid-title">
            <div>S{weekNumber}</div>
            {days.map((day, index) => {
              let classNameBtn = ['ant-btn-calendar'];
              if (day.format('YYYYMMDD') === props.selectedDate.format('YYYYMMDD')) {
                classNameBtn.push('ant-btn-calendar-active');
              }

              return (
                <Button
                  key={index}
                  className={classNameBtn.join(' ')}
                  onClick={() => { onSelect(day) }}
                >
                  {day.format('ddd DD MMM')}
                </Button>
              );
            })}
            <div
              style={{
                position: "relative",
                bottom: 4,
                textAlign: "center"
              }}
            >
              Total
            </div>
          </div>
          <div className="grid-data">
            <div className="row-title">Occupation</div>
            {stats.map((statOfDay, index) => {
              let classStat = ["row-data", "first", statOfDay['className']];
              return (
                <div key={index} className={classStat.join(' ')}>
                  {(statOfDay['occupancy_rate'] * 100).toFixed(2)}%
                </div>
              );
            })}
            <div className="row-data first"></div>
          </div>
          <div className="grid-data">
            <div className="row-title">En cours</div>
            {stats.map((statOfDay, index) => {
              let classStat = ["row-data", statOfDay['className']];
              return (
                <div key={index} className={classStat.join(' ')}>
                  {statOfDay['current']}
                </div>
              );
            })}
            <div className="row-data"></div>
          </div>
          <div className="grid-data">
            <div className="row-title">Entrées</div>
            {stats.map((statOfDay, index) => {
              let classStat = ["row-data", statOfDay['className']];
              return (
                <div key={index} className={classStat.join(' ')}>
                  {statOfDay['start']}
                </div>
              );
            })}
            <div className="row-data">{totals['start']}</div>
          </div>
          <div className="grid-data">
            <div className="row-title">Sorties</div>
            {stats.map((statOfDay, index) => {
              let classStat = ["row-data", statOfDay['className']];
              return (
                <div key={index} className={classStat.join(' ')}>
                  {statOfDay['total_end']}
                </div>
              );
            })}
            <div className="row-data">{totals['total_end']}</div>
          </div>
          <div className="grid-data">
            <div className="row-title">SIIPS</div>
            {stats.map((statOfDay, index) => {
              let classStat = ["row-data", "last", statOfDay['className']];
              return (
                <div key={index} className={classStat.join(' ')}>
                  {statOfDay['siips']['base'] + statOfDay['siips']['technical'] + statOfDay['siips']['relational']}
                </div>
              );
            })}
            <div className="row-data last"></div>
          </div>
        </div>
      </Spin>
    </>
  );
});

export default WeekTab;
